import React, { useState, useEffect } from 'react';
import '../App.css'; // Import App.css to use the styles

// Utility to create a slug from a string
function slugify(text) {
  return text
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .toLowerCase();
}

// Utility to get API base URL depending on environment
const getApiBaseUrl = () => {
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  ) {
    return 'http://localhost:5000';
  }
  // Use relative URL for production
  return '';
};


const ProjectsGrid = ({ projects, onProjectClick, hideIcons = false, likedProjectIds = [], onLikeRequireLogin }) => {
  // Determine login state internally
  const isLoggedIn = !!localStorage.getItem('token');

  const [projectLikes, setProjectLikes] = useState(
    projects.reduce((acc, project) => {
      acc[project.id] = project.likes || 0;
      return acc;
    }, {})
  );

  // For guests, store likes in localStorage; for logged-in, rely on likedProjectIds from parent
  const [guestLikedProjects, setGuestLikedProjects] = useState(() => {
    const storedLikes = localStorage.getItem('likedProjects');
    return storedLikes ? JSON.parse(storedLikes) : [];
  });

  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.setItem('likedProjects', JSON.stringify(guestLikedProjects));
    }
  }, [guestLikedProjects, isLoggedIn]);

  // Optionally notify parent to update likedProjectIds for connected users
  const handleLike = (projectId) => {
    if (!isLoggedIn && guestLikedProjects.includes(projectId)) {
      alert('Vous avez déjà aimé ce projet !');
      return;
    }
    if (isLoggedIn && likedProjectIds && likedProjectIds.includes(projectId)) {
      alert('Vous avez déjà aimé ce projet !');
      return;
    }

    fetch(`${getApiBaseUrl()}/api/projects/${projectId}/like`, {
      method: 'POST',
      headers: isLoggedIn ? {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      } : {},
      credentials: isLoggedIn ? 'include' : undefined,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setProjectLikes((prevLikes) => ({
            ...prevLikes,
            [projectId]: (prevLikes[projectId] || projects.find((p) => p.id === projectId).likes || 0) + 1,
          }));
          if (isLoggedIn) {
            // Immediately disable the heart by updating likedProjectIds locally
            if (typeof updateLikedProjectIds === 'function') {
              updateLikedProjectIds(projectId);
            }
          } else {
            setGuestLikedProjects((prevLiked) => [...prevLiked, projectId]);
          }
        }
      })
      .catch((error) => console.error('Error liking project:', error));
  };

  const handleShare = (projectId, projectName) => {
    const slug = slugify(projectName);
    const projectUrl = `${window.location.origin}/project/${projectId}-${slug}`;
    navigator.clipboard.writeText(projectUrl).then(() => {
      alert('Project link copied to clipboard!');
    });
  };

 
  const [justLiked, setJustLiked] = useState([]);
  const isProjectLiked = (projectId) => {
    if (isLoggedIn) {
      return (likedProjectIds && likedProjectIds.includes(projectId)) || justLiked.includes(projectId);
    }
    return false;
  };

  // Update justLiked when like is performed
  const updateLikedProjectIds = (projectId) => {
    setJustLiked((prev) => [...prev, projectId]);
  };

  // Unified heart click handler
  const handleHeartClick = (projectId, e) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      if (typeof onLikeRequireLogin === 'function') {
        onLikeRequireLogin();
      }
      return;
    }
    if (!isProjectLiked(projectId)) {
      handleLike(projectId);
    }
  };

  return (
    <div className="projectsGrid">
      {projects.map((project) => (
        <div key={project.id} className="projectContainer">
          <div
            className="projectItem"
            onClick={() => {
              // Always pass a valid slug to onProjectClick
              const slug = slugify(project.name);
              if (slug && slug !== 'undefined') {
                onProjectClick && onProjectClick(slug);
              }
            }}
          >
            {/* Project Image */}
            <div
              className="afterPhoto"
              style={{
                backgroundImage: `url(${project.after_photo})`,
              }}
            ></div>
          </div>


          {/* Project Info Section */}
          <div className="projectInfo" style={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            {/* Project Name */}
            <p className="projectName" style={{ marginBottom: 6 }}>{project.name}</p>

            {/* Dedicated new line for icons */}
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 16, margin: '8px 0 4px 0', width: '100%' }}>
              {!hideIcons && (
                <>
                  <div
                    className="heartContainer"
                    onClick={(e) => handleHeartClick(project.id, e)}
                    style={{
                      // For guests, always clickable; for logged-in, disable if already liked
                      pointerEvents: isLoggedIn && isProjectLiked(project.id) ? 'none' : 'auto',
                      opacity: isLoggedIn && isProjectLiked(project.id) ? 0.5 : 1,
                    }}
                    title={isLoggedIn && isProjectLiked(project.id) ? "Déjà aimé" : "Aimer"}
                  >
                    <img
                      src="/assets/site/heart.svg"
                      alt={isLoggedIn && isProjectLiked(project.id) ? "Icône cœur déjà aimé" : "Icône cœur pour aimer le projet"}
                      className="heartIcon"
                    />
                    <p className="likesCount">
                      {projectLikes[project.id] ?? project.likes ?? 0}
                    </p>
                  </div>
                  <div className="difficultyContainer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {(() => {
                      // Green color filter (same as heart icon)
                      const greenFilter = 'invert(52%) sepia(99%) saturate(749%) hue-rotate(74deg) brightness(92%) contrast(92%)';
                      switch (project.difficulty) {
                        case 1:
                          return (
                            <img
                              src="/assets/site/low-level.svg"
                              alt="Difficulté faible"
                              className="difficultyIcon"
                              style={{ width: '40px', height: '40px', filter: greenFilter }}
                              title="Difficulté faible"
                            />
                          );
                        case 2:
                          return (
                            <img
                              src="/assets/site/medium-level.svg"
                              alt="Difficulté moyenne"
                              className="difficultyIcon"
                              style={{ width: '40px', height: '40px', filter: greenFilter }}
                              title="Difficulté moyenne"
                            />
                          );
                        case 3:
                          return (
                            <img
                              src="/assets/site/high-level.svg"
                              alt="Difficulté élevée"
                              className="difficultyIcon"
                              style={{ width: '40px', height: '40px', filter: greenFilter }}
                              title="Difficulté élevée"
                            />
                          );
                        default:
                          return (
                            <span style={{ fontSize: '25px', fontWeight: 'bold', color: '#1ec31e' }} title="Difficulté inconnue">?</span>
                          );
                      }
                    })()}
                  </div>
                  <div
                    className="shareContainer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare(project.id, project.name);
                    }}
                    style={{ marginLeft: 4 }}
                    title="Partager"
                  >
                    <img
                      src="/assets/site/share.svg"
                      alt="Partager le projet"
                      className="shareIcon"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Share Popup removed as per new requirements */}
        </div>
      ))}
    </div>
  );
};

export default ProjectsGrid;